import {
  type ComponentProps,
  type FC,
  memo,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion, useAnimate } from 'framer-motion';

import { type MiniGameType } from '@/common/types/dice-game';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../../context';
import { DiceGameModalsContext } from '../../DiceGameModals/context';
import { type ParseCellContentReturnType } from '../../lib';
import { CardSelector } from '../CardSelector/CardSelector';

import { CardFront } from './CardFront';

import css from './styles.module.scss';

interface CardProps {
  id: string;
  kind: Omit<MiniGameType, 'tgem' | 'str'>;
  getCardReward: (args: { cellId: string }) => Promise<{
    card: ParseCellContentReturnType;
    withBonus?: boolean;
    isCaboom?: boolean;
    isStop?: boolean;
  }>;
  openCardData?: ComponentProps<typeof CardSelector>;
  disabled?: boolean;
  changeEnabledToOpenStatus: (isEnabledToOpen: boolean) => void;
}

const Card: FC<CardProps> = memo(
  ({
    kind,
    getCardReward,
    id,
    openCardData,
    disabled,
    changeEnabledToOpenStatus,
  }) => {
    const { openBombEvent, openGameOverEvent } = useContext(
      DiceGameModalsContext,
    );
    const [cardData, setCardData] = useState<ComponentProps<
      typeof CardSelector
    > | null>(openCardData || null);
    const [isCardOpened, setIsCardOpened] = useState(false);
    const [isCardInOpenedProcess, setIsCardInOpenedProcess] = useState(false);
    const [scope, animate] = useAnimate();
    const { t } = useTranslation();
    const { isFlyingCardsReward } = useContext(DiceGameContext);
    const skipAnimation = useAppSelector(
      (state) => state.diceGame.skipAnimation,
    );

    const openCard = async (): Promise<void> => {
      if (isCardInOpenedProcess || disabled || !!isFlyingCardsReward) return;

      try {
        changeEnabledToOpenStatus(false);
        setIsCardInOpenedProcess(true);
        const result = await getCardReward({ cellId: id });

        if (!(result.isStop || result.isCaboom)) {
          changeEnabledToOpenStatus(true);
        }

        setCardData(result.card);
        setIsCardOpened(true);
        await animate(
          scope.current,
          { rotateY: '180deg' },
          { duration: skipAnimation ? 0 : 0.5 },
        );

        if (result.isStop) {
          openGameOverEvent();
        }

        if (result.isCaboom) {
          openBombEvent();
        }
      } catch (error) {
        showToast(t('Something went wrong, please try again'));
        console.error(error);
        changeEnabledToOpenStatus(true);
      }
    };

    return (
      <div onClick={openCard} className={css.card}>
        <motion.div
          initial={openCardData ? { rotateY: '180deg' } : undefined}
          ref={scope}
          className={css.cardInner}
        >
          <CardFront className={css.sidePositionFront} kind={kind} />
          {cardData && (
            <CardSelector
              kind={cardData.kind}
              props={{
                ...cardData.props,
                isActive: isCardOpened,
                className: cn(css.sidePosition, css.cardBack),
              }}
            />
          )}
        </motion.div>
      </div>
    );
  },
);

export { Card };
